import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { COMPANY_CONFIG } from '../config/config';
import LoginModal from './LoginModal';
import PasswordResetModal from './PasswordResetModal';
import WebsiteLoading from './WebsiteLoading';
import { 
  Menu, 
  X, 
  Home, 
  Bus, 
  MapPin, 
  Calendar, 
  Users, 
  LogOut,
  User,
  Phone,
  Mail,
  Plus,
  Database,
  Ticket,
  Building2,
  ChevronDown,
  Key,
  UserCircle,
  ArrowRight
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout, isRole, ROLES, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Navigation - Only Home, About, and Contact for SEO
  const getNavigation = () => {
    return [
      { name: 'Home', href: '/', icon: Home },
      { name: 'About Us', href: '/about', icon: Users },
      { name: 'Contact Us', href: '/contact', icon: Phone },
    ];
  };

  const navigation = getNavigation();

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    setSidebarOpen(false);
    setUserDropdownOpen(false);
    // Redirect to landing page after logout
    navigate('/');
  };

  const handlePasswordReset = () => {
    setPasswordResetModalOpen(true);
    setUserDropdownOpen(false);
  };

  const handleUserDropdownToggle = () => {
    setUserDropdownOpen(!userDropdownOpen);
  };

  // Close dropdown when clicking outside
  const handleClickOutside = (e) => {
    if (userDropdownOpen && !e.target.closest('.user-dropdown')) {
      setUserDropdownOpen(false);
    }
  };

  // Add event listener for clicking outside
  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userDropdownOpen]);

  // Show website loading while app is initializing
  if (loading) {
    return <WebsiteLoading />;
  }

  return (
    <div className="min-h-screen w-screen min-w-screen max-w-screen bg-gray-50 overflow-x-hidden">
      {/* Header */}
      <header className="bg-white shadow-lg border-b border-gray-200 fixed top-0 left-0 right-0 z-50 w-screen min-w-screen max-w-screen">
        <div className="w-full px-2 sm:px-3 lg:px-6">
          <div className="flex justify-between items-center h-12 sm:h-14 lg:h-16">
            {/* Logo */}
            <div className="flex items-center flex-1 min-w-0">
              <div className="flex-shrink-0">
                <Link to="/" className="flex items-center group">
                  <img 
                    src="/images/Logo.png" 
                    alt={`${COMPANY_CONFIG.appname} Logo`} 
                    className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 object-contain mr-1.5 sm:mr-2"
                  />
                  <div className="min-w-0">
                    <div className="text-xs sm:text-sm lg:text-lg font-extrabold text-[#00336a] drop-shadow-lg truncate">
                      {COMPANY_CONFIG.appname}
                    </div>
                    <div className="text-[9px] sm:text-[10px] lg:text-xs text-gray-600 font-medium truncate hidden sm:block">
                      {COMPANY_CONFIG.taglinelogo}
                    </div>
                  </div>
                </Link>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`inline-flex items-center px-2 lg:px-3 py-1 lg:py-1.5 text-xs lg:text-sm font-semibold rounded-md lg:rounded-lg transition-all duration-200 ${
                      isActive(item.href)
                        ? 'bg-[#00336a] text-white shadow-md'
                        : 'text-[#00336a] hover:bg-gray-100 hover:text-[#00336a]'
                    }`}
                  >
                    <Icon className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-1.5" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            {/* User Menu */}
            <div className="hidden lg:flex items-center space-x-2">
              {/* Booking Button */}
              <a
                href="https://tripimate.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-gradient-to-r from-red-500 via-red-600 to-red-700 text-white px-2.5 lg:px-3 py-1 lg:py-1.5 rounded-md lg:rounded-lg text-xs lg:text-sm font-bold hover:from-red-600 hover:via-red-700 hover:to-red-800 transition-all duration-300 shadow-md hover:shadow-lg border border-red-400"
              >
                <span>Book Now</span>
              </a>
              
              {user ? (
                <div className="relative user-dropdown">
                  <button
                    onClick={handleUserDropdownToggle}
                    className="flex items-center space-x-1.5 bg-[#00336a] hover:bg-blue-800 rounded-md lg:rounded-lg px-2 lg:px-2.5 py-1 lg:py-1.5 transition-all duration-200"
                  >
                    <div className="w-6 h-6 lg:w-7 lg:h-7 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-[#00336a]" />
                    </div>
                    <div className="text-left min-w-0 hidden lg:block">
                      <div className="text-xs font-semibold text-white truncate max-w-[80px]">{user.fullName || user.name || user.username}</div>
                      <div className="text-[9px] text-blue-200 truncate">{user.roleName}</div>
                    </div>
                    <ChevronDown className={`w-3 h-3 text-white transition-transform flex-shrink-0 ${userDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {userDropdownOpen && (
                    <div className="absolute right-0 mt-1.5 w-48 lg:w-52 bg-white rounded-lg shadow-lg border border-gray-200 py-1.5 z-50">
                      {/* User Info */}
                      <div className="px-2.5 lg:px-3 py-1.5 lg:py-2 border-b border-gray-100 bg-gray-50">
                        <div className="flex items-center space-x-2">
                          <div className="w-7 h-7 lg:w-8 lg:h-8 bg-[#00336a] rounded-full flex items-center justify-center shadow-md flex-shrink-0">
                            <User className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-white" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-gray-900 text-xs lg:text-sm truncate">{user.fullName || user.name || user.username}</div>
                            <div className="text-[10px] lg:text-xs text-gray-600 truncate">{user.email || user.username || 'No email'}</div>
                            <div className="text-[9px] lg:text-[10px] text-[#00336a] font-bold truncate">{user.roleName}</div>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="py-1">
                        {user && !isRole(ROLES.PASSENGER) && (
                        <button
                          onClick={handlePasswordReset}
                          className="w-full flex items-center space-x-2 px-2.5 lg:px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <Key className="w-3 h-3 flex-shrink-0" />
                          <span>Reset Password</span>
                        </button>
                        )}
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center space-x-2 px-2.5 lg:px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="w-3 h-3 flex-shrink-0" />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1.5 text-xs lg:text-sm text-[#00336a] bg-gray-100 px-2 lg:px-2.5 py-1 lg:py-1.5 rounded-md lg:rounded-lg border border-gray-200">
                    <Phone className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-[#00336a] flex-shrink-0" />
                    <span className="font-semibold text-[#00336a] truncate max-w-[100px] lg:max-w-none">{COMPANY_CONFIG.phone}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu - Booking Button and Menu */}
            <div className="lg:hidden flex items-center space-x-1.5">
              <a
                href="https://tripimate.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-gradient-to-r from-red-500 via-red-600 to-red-700 text-white px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-md text-xs font-bold hover:from-red-600 hover:via-red-700 hover:to-red-800 transition-all duration-300 shadow-sm hover:shadow-md border border-red-400"
              >
                <span>Book Now</span>
              </a>
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="text-[#00336a] bg-gray-100 p-1 sm:p-1.5 rounded-md hover:bg-gray-200 transition-all duration-200"
              >
                {sidebarOpen ? <X className="w-4 h-4 sm:w-5 sm:h-5" /> : <Menu className="w-4 h-4 sm:w-5 sm:h-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setSidebarOpen(false)}
          />
          
          {/* Sidebar */}
          <div className="fixed right-0 top-0 h-full w-[75vw] sm:w-72 bg-white shadow-xl transform transition-transform duration-300 ease-in-out border-l border-gray-200">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
                <h2 className="text-base sm:text-lg font-extrabold text-[#00336a]">Menu</h2>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="text-[#00336a] hover:bg-gray-100 p-1 sm:p-1.5 rounded-md transition-colors"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
              
              {/* Navigation */}
              <nav className="flex-1 px-3 sm:px-4 py-2 sm:py-3 overflow-y-auto">
                <div className="space-y-1 sm:space-y-1.5">
                  {navigation.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                          isActive(item.href)
                            ? 'bg-[#00336a] text-white shadow-md'
                            : 'text-[#00336a] hover:bg-gray-100'
                        }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Icon className="w-4 h-4 mr-2 sm:mr-2.5" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </nav>
              
              {/* Footer */}
              <div className="p-3 sm:p-4 border-t border-gray-200">
                {user ? (
                  <div className="space-y-2 sm:space-y-3">
                    {/* User Info */}
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-2 mb-2">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#00336a] rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                        </div>
                        <div className="text-left min-w-0 flex-1">
                          <div className="font-semibold text-[#00336a] text-xs sm:text-sm truncate">{user.fullName || user.name || user.username}</div>
                          <div className="text-[10px] sm:text-xs text-gray-600 truncate">{user.email || user.username || 'No email'}</div>
                        </div>
                      </div>
                      <div className="px-2 py-0.5 bg-gray-100 text-[#00336a] rounded-full text-xs font-semibold mb-2 sm:mb-3 border border-gray-200 inline-block">
                        {user.roleName}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-1.5 sm:space-y-2">
                      <a
                        href="https://tripimate.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full inline-flex items-center justify-center bg-[#00336a] text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-bold hover:bg-blue-800 transition-all duration-300 shadow-sm hover:shadow-md border border-blue-700"
                      >
                        <span>Book Now</span>
                      </a>
                      {user && !isRole(ROLES.PASSENGER) && (
                      <button
                        onClick={handlePasswordReset}
                        className="w-full flex items-center justify-center space-x-1.5 px-3 py-1.5 bg-gray-100 text-[#00336a] rounded-lg hover:bg-gray-200 border border-gray-200 transition-all duration-200 text-xs sm:text-sm"
                      >
                        <Key className="w-3.5 h-3.5" />
                        <span>Reset Password</span>
                      </button>
                      )}
                      <button
                        onClick={handleLogout}
                        className="w-full bg-red-500 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg hover:bg-red-600 transition-all duration-200 shadow-sm flex items-center justify-center space-x-1.5 text-xs sm:text-sm font-semibold"
                      >
                        <LogOut className="w-3.5 h-3.5" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center space-y-2 sm:space-y-3">
                    <div className="flex items-center justify-center space-x-1.5 text-xs text-[#00336a] bg-gray-100 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg border border-gray-200">
                      <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                      <span className="font-semibold truncate">{COMPANY_CONFIG.phone}</span>
                    </div>
                    <a
                      href="https://tripimate.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center bg-[#00336a] text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-bold hover:bg-blue-800 transition-all duration-300 shadow-sm hover:shadow-md border border-blue-700 w-full"
                    >
                      <span>Book Now</span>
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="w-screen min-w-screen max-w-screen min-h-screen pt-12 sm:pt-14 lg:pt-16">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#00336a] text-white w-screen min-w-screen max-w-screen relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 sm:w-80 sm:h-80 bg-blue-900 rounded-full opacity-10 blur-2xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 sm:w-80 sm:h-80 bg-blue-900 rounded-full opacity-10 blur-2xl"></div>
        
        <div className="relative z-10 w-full py-6 sm:py-8 lg:py-10 px-3 sm:px-4 lg:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-4 sm:mb-6 lg:mb-8">
              {/* Company Info */}
              <div className="lg:col-span-2">
                <div className="flex items-center mb-3 sm:mb-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg overflow-hidden mr-2 sm:mr-3 bg-white p-1 sm:p-1.5 flex-shrink-0">
                    <img 
                      src="/images/Logo.png" 
                      alt={`${COMPANY_CONFIG.name} Logo`} 
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-sm sm:text-base lg:text-lg font-extrabold text-white block truncate">{COMPANY_CONFIG.name}</span>
                    <p className="text-[10px] sm:text-xs text-blue-200 font-medium truncate">{COMPANY_CONFIG.taglinelogo}</p>
                  </div>
                </div>
                <p className="text-blue-100 mb-3 sm:mb-4 lg:mb-5 text-xs sm:text-sm lg:text-base leading-relaxed max-w-2xl">
                  Luxury seating, smooth travel, and passenger service that makes every journey special.
                </p>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-1.5 sm:space-y-0 sm:space-x-3 lg:space-x-6">
                  <div className="flex items-center bg-white/10 backdrop-blur-sm px-2.5 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-md sm:rounded-lg border border-white/20 hover:bg-white/20 transition-all duration-300">
                    <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-blue-200 flex-shrink-0" />
                    <span className="text-white font-semibold text-xs sm:text-sm truncate">{COMPANY_CONFIG.phone}</span>
                  </div>
                  <div className="flex items-center bg-white/10 backdrop-blur-sm px-2.5 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-md sm:rounded-lg border border-white/20 hover:bg-white/20 transition-all duration-300">
                    <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-blue-200 flex-shrink-0" />
                    <span className="text-white font-semibold text-xs sm:text-sm truncate">{COMPANY_CONFIG.email}</span>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="text-sm sm:text-base lg:text-lg font-extrabold mb-2 sm:mb-3 lg:mb-4 text-white">Quick Links</h3>
                <ul className="space-y-1.5 sm:space-y-2 lg:space-y-2.5">
                  <li>
                    <Link 
                      to="/" 
                      className="text-blue-100 hover:text-white transition-all duration-300 text-xs sm:text-sm lg:text-base font-medium flex items-center group"
                    >
                      <ArrowRight className="w-3 h-3 mr-1.5 group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
                      <span>Home</span>
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/about" 
                      className="text-blue-100 hover:text-white transition-all duration-300 text-xs sm:text-sm lg:text-base font-medium flex items-center group"
                    >
                      <ArrowRight className="w-3 h-3 mr-1.5 group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
                      <span>About Us</span>
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/contact" 
                      className="text-blue-100 hover:text-white transition-all duration-300 text-xs sm:text-sm lg:text-base font-medium flex items-center group"
                    >
                      <ArrowRight className="w-3 h-3 mr-1.5 group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
                      <span>Contact Us</span>
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Bottom Section */}
            <div className="border-t border-white/20 pt-3 sm:pt-4 lg:pt-5">
              <div className="flex flex-col md:flex-row justify-between items-center space-y-1.5 sm:space-y-2 md:space-y-0 text-center md:text-left">
                <p className="text-blue-200 text-[10px] sm:text-xs lg:text-sm">
                  &copy; 2026 {COMPANY_CONFIG.name}. All rights reserved.
                </p>
                <p className="text-blue-200 text-[10px] sm:text-xs lg:text-sm">
                  Design and Developed by{' '}
                  <a 
                    href="https://www.visualsoftsolution.lk/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:text-blue-200 underline font-semibold transition-colors"
                  >
                    VSS
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      <LoginModal isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
      
      {/* Password Reset Modal */}
      <PasswordResetModal 
        isOpen={passwordResetModalOpen} 
        onClose={() => setPasswordResetModalOpen(false)} 
        userId={user?.userID || user?.id || user?.userId}
      />
    </div>
  );
};

export default Layout; 